<script setup>
import { onMounted, ref, computed, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRoute, useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import WorkflowCommentsPanel from "@/components/workflows/WorkflowCommentsPanel.vue";
import {
  findDirectEditState,
  getDirectDatabaseEditModuleMeta,
  getDirectEditConfigTitle,
  normalizeDirectEditConfigs,
  normalizeDirectEditStates,
} from "@/utils/directDatabaseEdit";
import {
  buildStepConditionTree,
  buildSubmittedValuesForSourceStep,
  evaluateConditionTree,
  normalizeConditionalValue,
  normalizePicklistFieldValue,
} from "@/utils/workflowConditions";

const route = useRoute();
const router = useRouter();
const $loading = useLoading(loaderData);

const workflow = ref(null);
const currentStepIndex = ref(0);
const formSubmissions = ref({});
const loadingFormSubmissions = ref({});
const currentUser = ref(null);
const rejectDialog = ref(false);
const rejectReason = ref('');
const currentStepForAction = ref(null);
const resolvedContactName = ref('');
const resolvedAssigneeName = ref('');
const directEditDetails = ref({});
const loadingDirectEditDetails = ref({});

const conditionalFieldKey = (stepId, fieldId) => `${stepId}:${fieldId}`;


const resolveConditionalSourceStep = (conditionalStep) => {
  if (!workflow.value?.steps || !conditionalStep?.conditional_parent_step_id) return null;

  const configuredParentStepId = Number(conditionalStep.conditional_parent_step_id);

  return workflow.value.steps.find(item => Number(item.id) === configuredParentStepId)
    || workflow.value.steps.find(item => Number(item.parent_id) === configuredParentStepId)
    || null;
};

const getPicklistOptionLabel = (stepId, fieldId, fieldValue) => {
  const sourceStep = workflow.value?.steps?.find(step => Number(step.id) === Number(stepId));
  if (!sourceStep || !Array.isArray(sourceStep.forms)) return null;
  const normalizedInput = normalizePicklistFieldValue(fieldValue);
  const normalizedValue = String(normalizedInput ?? '').trim();

  for (const form of sourceStep.forms) {
    const field = (form.form_fields || []).find(item => Number(item.id) === Number(fieldId));
    if (!field) continue;

    const options = field.options || [];
    for (let index = 0; index < options.length; index++) {
      const option = options[index];

      if (option && typeof option === 'object') {
        const optionId = option.id ?? option.value ?? option.name;
        const optionLabel = option.name ?? option.value ?? option.id;

        if (String(optionId ?? '').trim() === normalizedValue || String(optionLabel ?? '').trim() === normalizedValue) {
          return optionLabel ? String(optionLabel) : (normalizedInput ? String(normalizedInput) : null);
        }

        continue;
      }

      const optionLabel = String(option).trim();
      const implicitValue = String(index + 1);
      if (optionLabel === normalizedValue || implicitValue === normalizedValue) {
        return String(option);
      }
    }

    return normalizedInput ? String(normalizedInput) : null;
  }

  return normalizedInput ? String(normalizedInput) : null;
};

const picklistSelections = computed(() => {
  const labels = {};
  const raws = {};

  if (!workflow.value?.steps || !workflow.value?.id) {
    return { labels, raws };
  }

  workflow.value.steps.forEach(step => {
    (step.forms || []).forEach(form => {
      const submissionKey = `${form.id}_${workflow.value.id}_${step.id}`;
      const submission = formSubmissions.value[submissionKey] || {};

      (form.form_fields || []).forEach(field => {
        if (field.type !== 'pick_list') return;

        const rawCandidate = submission[field.id] ?? submission[field.name];
        const rawValue = normalizePicklistFieldValue(rawCandidate);
        if (rawValue === undefined || rawValue === null || rawValue === '') return;

        const key = conditionalFieldKey(step.id, field.id);
        labels[key] = getPicklistOptionLabel(step.id, field.id, rawValue);
        raws[key] = rawValue;
      });
    });
  });

  return { labels, raws };
});

const isConditionalStepSatisfied = (step) => {
  try {
    if (!step?.is_conditional) return true;
    if (!step?.conditional_parent_step_id) return false;

    const sourceStep = resolveConditionalSourceStep(step);
    if (!sourceStep) return false;

    const conditionTree = buildStepConditionTree(step);
    if (!conditionTree) return false;

    const submittedValues = buildSubmittedValuesForSourceStep({
      sourceStepId: Number(sourceStep?.id ?? step.conditional_parent_step_id),
      sourceStep,
      picklistSelections: picklistSelections.value,
      getPicklistOptionLabel,
    });

    const matches = evaluateConditionTree(conditionTree, submittedValues);
    if (!matches) return false;

    if (step.conditional_trigger_mode === 'on_step_completion') {
      return sourceStep?.status === 'completed' || sourceStep?.status === 'approved';
    }

    return true;
  } catch (error) {
    console.error('Error evaluating conditional step:', step?.id, error);
    return false;
  }
};

const orderedWorkflowSteps = computed(() => {
  if (!workflow.value?.steps) return [];

  return [...workflow.value.steps].sort((left, right) => {
    const leftOrder = Number(left?.parent_id ?? left?.id ?? 0);
    const rightOrder = Number(right?.parent_id ?? right?.id ?? 0);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return Number(left?.id ?? 0) - Number(right?.id ?? 0);
  });
});

const visibleSteps = computed(() => {
  if (!orderedWorkflowSteps.value?.length) return [];
  return orderedWorkflowSteps.value.filter(step => !step.is_conditional || isConditionalStepSatisfied(step));
});

const isAdminUser = computed(() => ['admin', 'super_admin'].includes(currentUser.value?.role));

const stepHasApprovers = (step) => Array.isArray(step?.approvers) && step.approvers.length > 0;

const isApproverForStep = (step) => {
  if (!currentUser.value || !stepHasApprovers(step)) return false;

  return step.approvers.some(approver => Number(approver.user_id) === Number(currentUser.value.id));
};

const isStepAwaitingApproval = (step) => step?.status === 'in_review';

const shouldShowApprovalButtons = (step) => {
  if (!step || !isStepAwaitingApproval(step)) return false;
  return isAdminUser.value || isApproverForStep(step);
};

const approvalStatusMessage = (step) => {
  if (!isStepAwaitingApproval(step)) return '';

  return shouldShowApprovalButtons(step)
    ? 'This step is waiting for your approval.'
    : 'Pending for approval';
};

// Track collapse state for documents and recipients
const expandedSections = ref({});

const getWorkflowViewData = () => {
  const id = route.params.id;
  let loader = $loading.show();
  axios
    .get(`/workflows/${id}/process`)
    .then((response) => {
      if (response.status === "success") {
        workflow.value = response.data.workflow;
        resolvedContactName.value = '';
        resolvedAssigneeName.value = '';

        setInitialStepFromQuery();
        resolveWorkflowTargetNames();
        loadAllDirectEditDetails(workflow.value);
        
        // Load form submissions for all forms
        workflow.value.steps.forEach(step => {
          if (step.forms) {
            step.forms.forEach(form => {
              if (form.id) {
                loadFormSubmission(form.id, workflow.value.id, step.id);
              }
            });
          }
        });
      } else {
        toast.error(response.message, { autoClose: 3000 });
        router.push({ name: "workflows-list" });
      }
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to load workflow.", { autoClose: 3000 });
      }
      router.push({ name: "workflows-list" });
    })
    .finally(() => {
      loader.hide();
    });
};

const resolveWorkflowTargetNames = async () => {
  const contactId = workflow.value?.contact_id;
  const assigneeId = workflow.value?.for_user_id;

  if (contactId) {
    try {
      const response = await axios.get(`/contacts/${contactId}`, {
        params: { show: 'all' },
      });
      if (response.status === 'success' && response.data?.contact) {
        const contact = response.data.contact;
        resolvedContactName.value = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
      }
    } catch (error) {
      console.error('Failed to resolve contact name:', error);
    }
  }

  if (assigneeId) {
    try {
      const response = await axios.get(`/users/${assigneeId}`);
      if (response.status === 'success' && response.data?.user) {
        const user = response.data.user;
        resolvedAssigneeName.value = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
      }
    } catch (error) {
      console.error('Failed to resolve assignee name:', error);
    }
  }
};

const setInitialStepFromQuery = () => {
  if (!visibleSteps.value?.length) return;

  const stepId = route.query.step_id;
  if (!stepId) {
    if (currentStepIndex.value > visibleSteps.value.length - 1) {
      currentStepIndex.value = Math.max(visibleSteps.value.length - 1, 0);
    }
    return;
  }

  const index = visibleSteps.value.findIndex(step => String(step.id) === String(stepId));
  if (index >= 0) {
    currentStepIndex.value = index;
    scrollToTop();
  }
};

const loadFormSubmission = async (formId, workflowId, stepId) => {
  const submissionKey = `${formId}_${workflowId}_${stepId}`;
  if (loadingFormSubmissions.value[submissionKey]) return;
  
  loadingFormSubmissions.value[submissionKey] = true;
  
  try {
    const response = await axios.get('/workflow-form-submissions', {
      params: {
        form_id: formId,
        workflow_id: workflowId,
        workflow_step_id: stepId,
      }
    });
    
    if (response.status === 'success' && response.data.values) {
      formSubmissions.value[submissionKey] = response.data.values;
    }
  } catch (error) {
    console.error('Failed to load form submission:', error);
  } finally {
    loadingFormSubmissions.value[submissionKey] = false;
  }
};

const getDirectEditDetailKey = (stepId, itemKey) => `${stepId}:${itemKey}`;

const loadDirectEditDetails = async (step, directEditConfig, directEditState) => {
  const stepId = Number(step?.id || 0);
  const itemKey = String(directEditConfig?.item_key || '').trim();
  const recordId = Number(directEditState?.record_id || 0);
  const detailKey = getDirectEditDetailKey(stepId, itemKey);

  if (!stepId || !itemKey) return;

  if (!directEditConfig || !recordId) {
    directEditDetails.value = {
      ...directEditDetails.value,
      [detailKey]: null,
    };
    return;
  }

  if (loadingDirectEditDetails.value[detailKey]) return;

  loadingDirectEditDetails.value = {
    ...loadingDirectEditDetails.value,
    [detailKey]: true,
  };

  try {
    const response = await axios.get('/direct-database-edit/record', {
      params: {
        workflow_id: workflow.value.id,
        workflow_step_id: stepId,
        item_key: itemKey,
        record_id: recordId,
      },
    });

    if (response.status === 'success') {
      directEditDetails.value = {
        ...directEditDetails.value,
        [detailKey]: response.data,
      };
    }
  } catch (error) {
    console.error('Failed to load direct edit details:', error);
    directEditDetails.value = {
      ...directEditDetails.value,
      [detailKey]: null,
    };
  } finally {
    loadingDirectEditDetails.value = {
      ...loadingDirectEditDetails.value,
      [detailKey]: false,
    };
  }
};

const loadAllDirectEditDetails = async (workflowData) => {
  const steps = Array.isArray(workflowData?.steps) ? workflowData.steps : [];
  await Promise.all(
    steps
      .flatMap(step => normalizeDirectEditConfigs(step?.direct_edit_config).map(config => ({
        step,
        directEditConfig: config,
        directEditState: findDirectEditState(step?.direct_edit_state, config.item_key),
      })))
      .map(({ step, directEditConfig, directEditState }) => loadDirectEditDetails(step, directEditConfig, directEditState)),
  );
};

const downloadDocument = (document) => {
  if (document.url) {
    const link = window.document.createElement('a');
    link.href = document.url;
    link.target = '_blank';
    link.download = document.name || 'document.pdf';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }
};

const downloadSignedDocument = (document) => {
  if (document.file_path) {
    const link = window.document.createElement('a');
    link.href = document.file_path;
    link.target = '_blank';
    link.download = document.name || 'signed_document.pdf';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }
};

const generateMappedDocument = async (document) => {
  try {
    const response = await axios.post(`/workflow_documents/${document.id}/generate`);
    if (response.status === "success") {
      const pdfUrl = response.data.url;
      if (pdfUrl) {
        // Create download link
        const link = window.document.createElement('a');
        link.href = pdfUrl;
        link.target = '_blank';
        link.download = document.name || 'mapped_document.pdf';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        toast.error('Failed to generate mapped document', { autoClose: 3000 });
      }
    } else {
      toast.error(response.message || 'Failed to generate mapped document', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Error generating mapped document:', error);
    toast.error('An error occurred while generating the mapped document', { autoClose: 3000 });
  }
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString();
};

const getFormSubmission = (formId, workflowId, stepId) => {
  const submissionKey = `${formId}_${workflowId}_${stepId}`;
  return formSubmissions.value[submissionKey] || {};
};

const getFormFieldValue = (field, submission) => {
  const value = submission[field.id] || submission[field.name];
  
  if (!value) return 'Not provided';
  
  // Handle different field types
  switch (field.type) {
    case 'pick_list':
      // For pick_list, value is a single ID, find the corresponding option name
      if (field.options && field.options.length > 0) {
        const option = field.options.find(opt => opt.id == value);
        return option ? option.name : value;
      }
      return value;
    case 'multi_select':
      // For multi_select, value can be comma-separated IDs or array of IDs
      if (field.options && field.options.length > 0) {
        let selectedIds = [];
        if (Array.isArray(value)) {
          selectedIds = value;
        } else if (typeof value === 'string') {
          selectedIds = value.split(',').map(id => id.trim());
        } else {
          selectedIds = [value];
        }
        
        const selectedNames = selectedIds.map(id => {
          const option = field.options.find(opt => opt.id == id);
          return option ? option.name : id;
        });
        
        return selectedNames.join(', ');
      }
      return Array.isArray(value) ? value.join(', ') : value;
    case 'checkbox':
      return Array.isArray(value) ? value.join(', ') : value;
    case 'radio':
    case 'select':
      return value;
    case 'file':
      return value ? 'File uploaded' : 'No file';
    case 'date':
      return value ? new Date(value).toLocaleDateString() : 'No date';
    case 'datetime':
      return value ? new Date(value).toLocaleString() : 'No date';
    default:
      return value.toString();
  }
};

const getDirectEditModuleLabel = (directEditConfig) => {
  const entity = String(directEditConfig?.entity || '').trim();
  if (!entity) return 'Direct Database Edit';

  return getDirectDatabaseEditModuleMeta(entity)?.label || entity;
};

const getDirectEditConfigsForStep = (step) => {
  return normalizeDirectEditConfigs(step?.direct_edit_config);
};

const getDirectEditStateForStep = (step, itemKey) => {
  return findDirectEditState(step?.direct_edit_state, itemKey);
};

const getDirectEditPayload = (step, itemKey) => {
  return directEditDetails.value[getDirectEditDetailKey(Number(step?.id || 0), String(itemKey || ''))] || null;
};

const getDirectEditRecordLabel = (step, directEditConfig) => {
  const payload = getDirectEditPayload(step, directEditConfig?.item_key);
  if (payload?.record?.label) return payload.record.label;

  const recordId = Number(getDirectEditStateForStep(step, directEditConfig?.item_key)?.record_id || 0);
  if (!recordId) return 'No record selected';

  return `Record #${recordId}`;
};

const getDirectEditFields = (step, directEditConfig) => {
  const payload = getDirectEditPayload(step, directEditConfig?.item_key);
  return Array.isArray(payload?.fields) ? payload.fields : [];
};

const getDirectEditFieldValue = (field) => {
  const value = field?.value;

  if (field?.is_lookup_field && field?.display_value) {
    return field.display_value;
  }

  if (value === null || value === undefined || value === '') {
    return 'Not provided';
  }

  if (field?.type === 'pick_list') {
    const option = (field.options || []).find(opt => String(opt.id) === String(value));
    return option?.name || String(value);
  }

  if (field?.type === 'multi_select') {
    const values = Array.isArray(value)
      ? value
      : String(value).split(',').map(item => item.trim()).filter(Boolean);

    if (values.length === 0) return 'Not provided';

    return values.map(item => {
      const option = (field.options || []).find(opt => String(opt.id) === String(item));
      return option?.name || String(item);
    }).join(', ');
  }

  if (field?.type === 'checkbox') {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  if (field?.type === 'file') {
    return 'File uploaded';
  }

  if (field?.type === 'date') {
    return value ? new Date(value).toLocaleDateString() : 'No date';
  }

  if (field?.type === 'datetime') {
    return value ? new Date(value).toLocaleString() : 'No date';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
};

const getFieldIcon = (fieldType) => {
  const iconMap = {
    'text': 'tabler-abc',
    'email': 'tabler-mail',
    'number': 'tabler-123',
    'tel': 'tabler-phone',
    'url': 'tabler-link',
    'password': 'tabler-lock',
    'textarea': 'tabler-text-size',
    'select': 'tabler-chevron-down',
    'radio': 'tabler-circle-dot',
    'checkbox': 'tabler-checkbox',
    'file': 'tabler-paperclip',
    'date': 'tabler-calendar',
    'datetime': 'tabler-calendar-time',
    'time': 'tabler-clock',
    'hidden': 'tabler-eye-off',
  };
  
  return iconMap[fieldType] || 'tabler-forms';
};

const getRecipientStatusColor = (status) => {
  if (!status) return 'grey'
  
  status = status.toLowerCase()
  switch (status) {
    case 'completed':
      return 'success'
    case 'sent':
    case 'delivered':
      return 'info'
    case 'declined':
      return 'error'
    case 'pending':
    case 'processing':
      return 'warning'
    case 'autoresponded':
      return 'purple'
    default:
      return 'grey'
  }
};

// Check if all recipients have completed but envelope is still pending
const isProcessing = (envelope) => {
  if (!envelope || !envelope.recipients || envelope.recipients.length === 0) {
    return false;
  }
  
  if (envelope.status && envelope.status.toLowerCase() !== 'pending') {
    return false;
  }
  
  return envelope.recipients.every(recipient => 
    recipient.status && recipient.status.toLowerCase() === 'completed'
  );
};

// Get the display status for an envelope
const getEnvelopeDisplayStatus = (envelope) => {
  if (isProcessing(envelope)) {
    return 'Processing';
  }
  return envelope.status;
};

const toggleSection = (envelopeId, section) => {
  if (!expandedSections.value[envelopeId]) {
    expandedSections.value[envelopeId] = { documents: false, recipients: false };
  }
  expandedSections.value[envelopeId][section] = !expandedSections.value[envelopeId][section];
};

const isSectionExpanded = (envelopeId, section) => {
  if (!expandedSections.value[envelopeId]) {
    expandedSections.value[envelopeId] = { documents: false, recipients: false };
  }
  return expandedSections.value[envelopeId][section];
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const currentStep = computed(() => {
  if (!visibleSteps.value?.length) return null;
  return visibleSteps.value[currentStepIndex.value] || null;
});

const workflowProgressPercent = computed(() => {
  const total = visibleSteps.value?.length || 0;
  if (!total) return 0;
  const completed = visibleSteps.value.filter(step => ['completed', 'approved'].includes(step.status)).length;
  return Math.round((completed / total) * 100);
});

const workflowOwnerName = computed(() => {
  const owner = workflow.value?.user;
  if (!owner) return 'Unknown User';
  return owner.name || [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.email || `User #${owner.id}`;
});

const workflowContactDisplay = computed(() => {
  const contact = workflow.value?.contact;
  if (contact?.first_name || contact?.last_name) return [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  if (resolvedContactName.value) return resolvedContactName.value;
  if (workflow.value?.contact_id) return `Contact #${workflow.value.contact_id}`;
  return 'Not set';
});

const workflowAssigneeDisplay = computed(() => {
  const user = workflow.value?.for_user;
  if (user?.first_name || user?.last_name) return [user.first_name, user.last_name].filter(Boolean).join(' ');
  if (resolvedAssigneeName.value) return resolvedAssigneeName.value;
  if (workflow.value?.for_user_id) return `User #${workflow.value.for_user_id}`;
  return 'Not set';
});

const hasWorkflowContactTarget = computed(() => workflowContactDisplay.value !== 'Not set');
const hasWorkflowAssigneeTarget = computed(() => workflowAssigneeDisplay.value !== 'Not set');

const workflowTaskSummary = computed(() => {
  const stats = { total: 0, active: 0, pending: 0, completed: 0 };
  if (!visibleSteps.value?.length) return stats;
  visibleSteps.value.forEach(step => {
    const tasks = Array.isArray(step?.tasks) ? step.tasks : [];
    tasks.forEach(task => {
      stats.total += 1;
      if (task.status === 'completed') stats.completed += 1;
      else if (task.status === 'active' || task.status === 'in_progress') stats.active += 1;
      else stats.pending += 1;
    });
  });
  return stats;
});

const currentStepApprovers = computed(() => {
  const step = currentStep.value;
  if (!step || !Array.isArray(step.approvers)) return [];
  return step.approvers
    .filter(approver => approver?.user_id)
    .map(approver => {
      const fullName = [approver.first_name, approver.last_name].map(name => String(name || '').trim()).filter(Boolean).join(' ');
      return fullName || approver.email || `User #${approver.user_id}`;
    });
});

const stepStatusText = step => {
  if (!step?.status) return 'Pending';
  return step.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const goToStep = (index) => {
  currentStepIndex.value = index;
  scrollToTop();
};

const nextStep = () => {
  if (currentStepIndex.value < visibleSteps.value.length - 1) {
    currentStepIndex.value++;
    scrollToTop();
  }
};

const prevStep = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
    scrollToTop();
  }
};

const getCurrentUser = () => {
  axios.get('/auth/user')
    .then((response) => {
      if (response.status === 'success') {
        currentUser.value = response.data.user;
      }
    })
    .catch((error) => {
      console.error('Failed to fetch current user:', error);
    });
};

const approveStep = async (step) => {
  if (!step?.id || !shouldShowApprovalButtons(step)) return;

  let loader = $loading.show();

  try {
    const response = await axios.post(`/workflows/${workflow.value.id}/steps/${step.id}/approve`);

    if (response.status === 'success') {
      step.status = 'completed';
      step.reject_reason = null;
      step.rejected_by = null;
      step.rejected_at = null;

      if (response.data?.step?.approved_by) {
        step.approved_by = response.data.step.approved_by;
      }
      if (response.data?.step?.approved_at) {
        step.approved_at = response.data.step.approved_at;
      }

      toast.success('Step approved successfully.', { autoClose: 3000 });
    } else {
      toast.error(response.message || 'Failed to approve step.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to approve step:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to approve step.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const showRejectDialog = (step) => {
  if (!step?.id || !shouldShowApprovalButtons(step)) return;

  currentStepForAction.value = step;
  rejectReason.value = '';
  rejectDialog.value = true;
};

const rejectStep = async () => {
  if (!currentStepForAction.value?.id) return;
  if (!rejectReason.value.trim()) {
    toast.error('Please provide a reason for rejection.', { autoClose: 3000 });
    return;
  }

  let loader = $loading.show();

  try {
    const response = await axios.post(`/workflows/${workflow.value.id}/steps/${currentStepForAction.value.id}/reject`, {
      reject_reason: rejectReason.value,
    });

    if (response.status === 'success') {
      currentStepForAction.value.status = 'rejected';
      currentStepForAction.value.reject_reason = rejectReason.value;

      if (response.data?.step?.rejected_by) {
        currentStepForAction.value.rejected_by = response.data.step.rejected_by;
      }
      if (response.data?.step?.rejected_at) {
        currentStepForAction.value.rejected_at = response.data.step.rejected_at;
      }

      rejectDialog.value = false;
      toast.success('Step rejected successfully.', { autoClose: 3000 });
    } else {
      toast.error(response.message || 'Failed to reject step.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to reject step:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to reject step.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

onMounted(() => {
  getWorkflowViewData();
  getCurrentUser();
});

watch(
  () => [workflow.value, route.query.step_id, visibleSteps.value.length],
  () => {
    setInitialStepFromQuery();
  }
);

watch(visibleSteps, (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) {
    currentStepIndex.value = 0;
    return;
  }

  if (currentStepIndex.value > steps.length - 1) {
    currentStepIndex.value = steps.length - 1;
  }
}, { deep: true });

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div v-if="workflow">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2 class="text-h4 font-weight-bold mb-0">{{ workflow.title }}</h2>
      <VBtn color="primary" variant="outlined" @click="router.push({ name: 'workflows-list' })">
        <VIcon icon="tabler-arrow-left" start />
        Back to List
      </VBtn>
    </div>

    <VRow class="ma-0" align="start">
      <VCol cols="12" lg="3" class="py-2">
        <VCard class="wf-left-card" variant="outlined">
          <VCardText>
            <div class="d-flex align-center justify-space-between mb-3">
              <h3 class="text-h6 mb-0">Workflow Progress</h3>
              <span class="text-caption">Step {{ currentStepIndex + 1 }} of {{ visibleSteps.length }}</span>
            </div>
            <VProgressLinear :model-value="workflowProgressPercent" color="primary" height="8" rounded class="mb-1" />
            <div class="text-caption text-medium-emphasis mb-4">{{ workflowProgressPercent }}% complete</div>

            <div class="wf-step-tree">
              <button
                v-for="(step, index) in visibleSteps"
                :key="step.id"
                class="wf-tree-node"
                type="button"
                :class="{
                  active: index === currentStepIndex,
                  completed: ['completed', 'approved'].includes(step.status),
                }"
                @click="goToStep(index)"
              >
                <div class="wf-tree-rail">
                  <div class="wf-step-index">{{ index + 1 }}</div>
                  <div v-if="index < visibleSteps.length - 1" class="wf-step-line" />
                </div>
                <div class="wf-step-main">
                  <div class="wf-step-title">{{ step.title }}</div>
                  <div class="wf-step-sub">{{ stepStatusText(step) }}</div>
                </div>
                <div v-if="['completed', 'approved'].includes(step.status)" class="wf-step-tail">
                  <VIcon icon="tabler-circle-check-filled" color="success" size="20" />
                </div>
              </button>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="5" class="py-2">
        <VCard variant="outlined">
          <VCardText>
            <div v-if="currentStep">
          <div class="d-flex align-center gap-2 mb-4">
            <h3 class="text-h5">{{ currentStep.title }}</h3>
            <VChip 
              :color="['approved', 'completed'].includes(currentStep.status) ? 'success' : currentStep.status === 'rejected' ? 'error' : currentStep.status === 'in_review' ? 'info' : 'warning'" 
              size="small"
            >
              {{ currentStep.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
            </VChip>
          </div>
          <p class="mt-2 text-body-1 mb-6">{{ currentStep.description }}</p>

          <div v-if="isStepAwaitingApproval(currentStep)" class="mb-4">
            <VAlert
              :type="shouldShowApprovalButtons(currentStep) ? 'info' : 'warning'"
              variant="tonal"
              icon="tabler-user-check"
            >
              <VAlertTitle>Approval Required</VAlertTitle>
              <div class="mt-2">{{ approvalStatusMessage(currentStep) }}</div>
              <div v-if="shouldShowApprovalButtons(currentStep)" class="d-flex gap-2 mt-4">
                <VBtn
                  color="success"
                  size="small"
                  @click="approveStep(currentStep)"
                >
                  <VIcon icon="tabler-check" start />
                  Approve
                </VBtn>
                <VBtn
                  color="error"
                  size="small"
                  variant="outlined"
                  @click="showRejectDialog(currentStep)"
                >
                  <VIcon icon="tabler-x" start />
                  Reject
                </VBtn>
              </div>
            </VAlert>
          </div>

          <!-- Rejection Reason (if step is rejected) -->
          <div v-if="currentStep.status === 'rejected'" class="mb-4">
            <VAlert
              type="error"
              variant="tonal"
              icon="tabler-alert-circle"
            >
              <VAlertTitle>Step Rejected</VAlertTitle>
              <div class="mt-2">
                <strong>Reason:</strong> {{ currentStep.reject_reason || 'No reason provided' }}
              </div>
            </VAlert>
          </div>

          <div v-if="currentStep.approved_by || currentStep.rejected_by" class="mb-4">
            <!-- Approved By -->
            <div v-if="['approved', 'completed'].includes(currentStep.status) && currentStep.approved_by" class="mb-3">
              <VCard variant="outlined" color="success">
                <VCardText class="py-3">
                  <div class="d-flex align-center">
                    <VAvatar
                      size="32"
                      :variant="!currentStep.approved_by.user_profile ? 'tonal' : undefined"
                      :color="!currentStep.approved_by.user_profile ? 'success' : undefined"
                      class="mr-3"
                    >
                      <VImg
                        v-if="currentStep.approved_by.user_profile"
                        :src="currentStep.approved_by.user_profile"
                      />
                      <span v-else>{{ currentStep.approved_by.name?.charAt(0)?.toUpperCase() }}</span>
                    </VAvatar>
                    <div class="flex-grow-1">
                      <div class="d-flex align-center gap-2">
                        <VIcon icon="tabler-check" color="success" size="small" />
                        <span class="font-weight-medium text-success">Approved by {{ currentStep.approved_by.name }}</span>
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ currentStep.approved_by.email }} • {{ currentStep.approved_by.role_name }} • {{ formatDate(currentStep.approved_at) }}
                      </div>
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </div>

            <!-- Rejected By -->
            <div v-if="currentStep.status === 'rejected' && currentStep.rejected_by" class="mb-3">
              <VCard variant="outlined" color="error">
                <VCardText class="py-3">
                  <div class="d-flex align-center">
                    <VAvatar
                      size="32"
                      :variant="!currentStep.rejected_by.user_profile ? 'tonal' : undefined"
                      :color="!currentStep.rejected_by.user_profile ? 'error' : undefined"
                      class="mr-3"
                    >
                      <VImg
                        v-if="currentStep.rejected_by.user_profile"
                        :src="currentStep.rejected_by.user_profile"
                      />
                      <span v-else>{{ currentStep.rejected_by.name?.charAt(0)?.toUpperCase() }}</span>
                    </VAvatar>
                    <div class="flex-grow-1">
                      <div class="d-flex align-center gap-2">
                        <VIcon icon="tabler-x" color="error" size="small" />
                        <span class="font-weight-medium text-error">Rejected by {{ currentStep.rejected_by.name }}</span>
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ currentStep.rejected_by.email }} • {{ currentStep.rejected_by.role_name }} • {{ formatDate(currentStep.rejected_at) }}
                      </div>
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </div>
          </div>

          <!-- Forms Section -->
          <div v-if="currentStep.forms && currentStep.forms.length > 0" class="mb-6">
            <h4 class="text-h6 mb-4">Forms</h4>
            <div v-for="form in currentStep.forms" :key="form.id" class="mb-4">
              <VCard variant="outlined">
                <VCardTitle class="text-subtitle-1">
                  <VIcon icon="tabler-forms" class="mr-2" />
                  {{ form.title }}
                </VCardTitle>
                <VCardText>
                  <p class="text-body-2 mb-4">{{ form.description }}</p>
                  
                  <!-- Form Fields and Values -->
                  <div v-if="form.form_fields && form.form_fields.length > 0">
                    <div class="form-submission-grid">
                      <div 
                        v-for="field in form.form_fields" 
                        :key="field.id" 
                        class="field-item pa-3 border rounded mb-3"
                      >
                        <div class="d-flex align-center mb-2">
                          <VIcon :icon="getFieldIcon(field.type)" class="mr-2" size="small" color="primary" />
                          <span class="field-label font-weight-medium">{{ field.label || field.name }}</span>
                          <VChip v-if="field.is_required" size="x-small" color="error" class="ml-2">Required</VChip>
                        </div>
                        
                        <div class="field-value pa-2 bg-grey-lighten-5 rounded">
                          <span class="text-body-2">
                            {{ getFormFieldValue(field, getFormSubmission(form.id, workflow.id, currentStep.id)) }}
                          </span>
                        </div>
                        
                        <div v-if="field.description" class="field-description text-caption text-medium-emphasis mt-1">
                          {{ field.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-else-if="loadingFormSubmissions[`${form.id}_${workflow.id}_${currentStep.id}`]" class="text-center pa-4">
                    <VProgressCircular indeterminate color="primary" size="small" />
                    <span class="ml-2">Loading form submission...</span>
                  </div>
                  
                  <div v-else class="text-center pa-2 text-disabled">
                    No form fields available
                  </div>
                </VCardText>
              </VCard>
            </div>
          </div>

          <div v-if="getDirectEditConfigsForStep(currentStep).length > 0" class="mb-6">
            <h4 class="text-h6 mb-4">Direct Database Edit</h4>
            <VCard v-for="(directEditConfig, directEditIndex) in getDirectEditConfigsForStep(currentStep)" :key="directEditConfig.item_key || directEditIndex" variant="outlined" class="mb-4">
              <VCardTitle class="text-subtitle-1">
                <VIcon icon="tabler-database-edit" class="mr-2" />
                {{ getDirectEditConfigTitle(directEditConfig, directEditIndex) }}
              </VCardTitle>
              <VCardText>
                <div class="d-flex flex-wrap align-center gap-2 mb-4">
                  <VChip color="primary" variant="tonal">
                    <VIcon icon="tabler-database" start size="16" />
                    {{ getDirectEditRecordLabel(currentStep, directEditConfig) }}
                  </VChip>
                  <VChip v-if="getDirectEditStateForStep(currentStep, directEditConfig.item_key)?.updated_at" color="black" variant="tonal">
                    <VIcon icon="tabler-clock" start size="16" />
                    Updated {{ formatDate(getDirectEditStateForStep(currentStep, directEditConfig.item_key)?.updated_at) }}
                  </VChip>
                  <VChip size="x-small" variant="outlined">
                    {{ getDirectEditModuleLabel(directEditConfig) }}
                  </VChip>
                </div>

                <div v-if="loadingDirectEditDetails[getDirectEditDetailKey(currentStep.id, directEditConfig.item_key)]" class="text-center pa-4">
                  <VProgressCircular indeterminate color="primary" size="small" />
                  <span class="ml-2">Loading direct edit values...</span>
                </div>

                <div v-else-if="getDirectEditFields(currentStep, directEditConfig).length > 0" class="form-submission-grid">
                  <div
                    v-for="field in getDirectEditFields(currentStep, directEditConfig)"
                    :key="`${currentStep.id}-${directEditConfig.item_key}-${field.field_source}-${field.field_id || field.field}`"
                    class="field-item pa-3 border rounded mb-3"
                  >
                    <div class="d-flex align-center mb-2">
                      <VIcon :icon="getFieldIcon(field.type)" class="mr-2" size="small" color="primary" />
                      <span class="field-label font-weight-medium">{{ field.label }}</span>
                      <VChip size="x-small" variant="outlined" class="ml-2">
                        {{ field.field_source === 'custom' ? 'Custom Field' : 'Core Field' }}
                      </VChip>
                    </div>

                    <div class="field-value pa-2 bg-grey-lighten-5 rounded">
                      <span class="text-body-2">
                        {{ getDirectEditFieldValue(field) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div v-else-if="getDirectEditStateForStep(currentStep, directEditConfig.item_key)?.record_id" class="text-center pa-2 text-disabled">
                  No direct edit values available
                </div>

                <div v-else class="text-center pa-2 text-disabled">
                  No direct edit record has been selected for this step yet
                </div>
              </VCardText>
            </VCard>
          </div>

          <!-- Documents Section -->
          <div v-if="currentStep.document_templates && currentStep.document_templates.length > 0" class="mb-6">
            <h4 class="text-h6 mb-4">Documents</h4>
            <div v-for="template in currentStep.document_templates" :key="template.id" class="mb-4">
              <VCard variant="outlined">
                <VCardTitle class="text-subtitle-1">
                  <VIcon icon="tabler-file-text" class="mr-2" />
                  {{ template.name }}
                </VCardTitle>
                <VCardText>
                  <div v-if="template.documents && template.documents.length > 0" class="mb-4">
                    <h5 class="text-subtitle-2 mb-2">Template Documents:</h5>
                    <div v-for="doc in template.documents" :key="doc.id" class="d-flex align-center justify-space-between pa-2 border rounded mb-2">
                      <div class="d-flex align-center">
                        <VIcon icon="tabler-file-pdf" class="mr-2" color="error" />
                        <span>{{ doc.name }}</span>
                      </div>
                      <VBtn 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        @click="generateMappedDocument(doc)"
                      >
                        <VIcon icon="tabler-download" class="mr-1" />
                        Download
                      </VBtn>
                    </div>
                  </div>

                  <!-- Enhanced Envelopes Section -->
                  <div v-if="template.signed_documents && Object.keys(template.signed_documents).length > 0">
                    <h5 class="text-subtitle-2 mb-3">DocuSign Envelopes:</h5>
                    <div v-for="(envelope, envelopeId) in template.signed_documents" :key="envelopeId" class="envelope-card mb-4 pa-3 border rounded">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div>
                          <div class="envelope-title d-flex align-center">
                            <VIcon icon="tabler-mail" class="mr-2" color="primary" />
                            <h6 class="text-subtitle-2 mb-0">Envelope ID: <span class="font-weight-regular envelope-id">{{ envelopeId }}</span></h6>
                          </div>
                          <div class="mt-1 text-body-2">
                            <div v-if="envelope.created_at"><strong>Created:</strong> {{ envelope.created_at }}</div>
                          </div>
                        </div>
                        <VChip 
                          size="small" 
                          :color="getRecipientStatusColor(isProcessing(envelope) ? 'processing' : envelope.status)" 
                          class="text-capitalize"
                        >
                          {{ getEnvelopeDisplayStatus(envelope) }}
                          <VProgressCircular v-if="isProcessing(envelope)" indeterminate size="12" width="2" color="white" class="ml-1" />
                        </VChip>
                      </div>
                      
                      <VDivider class="my-3" />
                      
                      <div>
                        <div class="d-flex justify-space-between align-center mb-2">
                          <h6 class="text-subtitle-2 mb-0 d-flex align-center">
                            <VIcon icon="tabler-file-text" class="mr-1" size="small" />
                            Documents ({{ envelope.documents ? envelope.documents.length : 0 }})
                          </h6>
                          <VBtn 
                            icon
                            variant="text"
                            size="small" 
                            @click="toggleSection(envelopeId, 'documents')"
                            class="ml-auto"
                          >
                            <VIcon :icon="isSectionExpanded(envelopeId, 'documents') ? 'tabler-chevron-up' : 'tabler-chevron-down'" />
                          </VBtn>
                        </div>
                        <div v-if="isSectionExpanded(envelopeId, 'documents')">
                          <div class="documents-list">
                            <div v-for="(doc, idx) in envelope.documents" :key="idx" class="document-item pa-2 d-flex justify-space-between align-center border rounded mb-2">
                              <div class="d-flex align-center">
                                <VIcon icon="tabler-file-pdf" class="mr-2" color="error" size="small" />
                                <div class="document-name">{{ doc.name || 'Document ' + (idx + 1) }}</div>
                              </div>
                              <VBtn 
                                color="primary" 
                                size="small" 
                                variant="outlined"
                                @click="downloadSignedDocument(doc)"
                                class="ml-2"
                              >
                                <VIcon icon="tabler-download" size="small" class="mr-1" />
                                Download
                              </VBtn>
                            </div>
                            <div v-if="!envelope.documents || envelope.documents.length === 0" class="pa-2 text-center text-disabled">
                              No documents available
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-else class="text-center pa-2 text-disabled">
                    No envelopes found
                  </div>
                </VCardText>
              </VCard>
            </div>
          </div>
              <div class="d-flex justify-space-between mt-6">
                <VBtn variant="text" :disabled="currentStepIndex === 0" @click="prevStep">
                  <VIcon icon="tabler-arrow-left" start />
                  Previous Step
                </VBtn>

                <VBtn v-if="currentStepIndex < visibleSteps.length - 1" color="primary" @click="nextStep">
                  Next Step
                  <VIcon icon="tabler-arrow-right" end />
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" lg="4" class="py-2">
        <div class="wf-right-stack">
          <VCard variant="outlined">
            <VCardText>
              <div class="d-flex justify-space-between align-center mb-3">
                <h3 class="text-h6 mb-0">Workflow Information</h3>
              </div>
              <div class="wf-kv"><span>Owner</span><strong>{{ workflowOwnerName }}</strong></div>
              <div class="wf-kv"><span>Status</span><strong class="text-capitalize">{{ workflow.status.replace(/_/g, ' ') }}</strong></div>
            </VCardText>
          </VCard>

          <VCard variant="outlined">
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-h6 mb-0">Target Information</h3>
              </div>
              <div class="wf-chip-wrap" v-if="hasWorkflowContactTarget || hasWorkflowAssigneeTarget">
                <VChip v-if="hasWorkflowContactTarget" color="primary" variant="elevated" class="text-white font-weight-medium">
                  <VIcon icon="tabler-user" start size="18" />
                  {{ workflowContactDisplay }}
                </VChip>
                <VChip v-if="hasWorkflowAssigneeTarget" color="secondary" variant="elevated" class="text-white font-weight-medium">
                  <VIcon icon="tabler-user-check" start size="18" />
                  {{ workflowAssigneeDisplay }}
                </VChip>
              </div>
            </VCardText>
          </VCard>

          <VCard v-if="currentStepApprovers.length > 0" variant="outlined">
            <VCardText>
              <h3 class="text-h6 mb-3">Step Approver</h3>
              <div class="wf-chip-wrap">
                <VChip v-for="approver in currentStepApprovers" :key="approver" color="info" variant="tonal">
                  {{ approver }}
                </VChip>
              </div>
            </VCardText>
          </VCard>

          <WorkflowCommentsPanel
            :workflow-id="workflow.id"
            :active-step="currentStep"
            :steps="workflow.steps || []"
            :read-only="true"
          />

        </div>
      </VCol>
    </VRow>
  </div>

  <div v-else class="text-center">
    <p>Loading workflow...</p>
  </div>

  <VDialog v-model="rejectDialog" max-width="500px" persistent>
    <VCard>
      <VCardTitle class="text-h5 mt-4">
        <VIcon icon="tabler-x" class="mr-2" color="error" />
        Reject Step
      </VCardTitle>
      <VCardText>
        <p class="text-body-1 mb-4">
          Provide a reason for rejecting this step.
        </p>
        <VTextarea
          v-model="rejectReason"
          label="Rejection Reason"
          placeholder="Explain why this step is being rejected"
          rows="4"
          counter="1000"
          :rules="[v => !!v || 'Rejection reason is required']"
        />
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          color="default"
          variant="outlined"
          @click="rejectDialog = false"
        >
          Cancel
        </VBtn>
        <VBtn
          color="error"
          @click="rejectStep"
          class="ml-3"
          :disabled="!rejectReason.trim()"
        >
          Reject Step
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.envelope-card {
  background-color: #ffffff;
  border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.envelope-id {
  font-family: monospace;
  word-break: break-all;
  max-width: 150px;
  background-color: #f5f5f5;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.9em;
}

.document-item {
  border-color: rgba(var(--v-theme-on-surface), 0.08) !important;
  background-color: #fafafa;
}

.document-name {
  font-weight: 500;
  max-width: 350px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recipient-item {
  border-radius: 4px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
}

.wf-step-tree {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding-right: 4px;
}

.wf-tree-node {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: stretch;
  gap: 10px;
  text-align: left;
  transition: all .16s ease;
  border-radius: 10px;
}

.wf-tree-node:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}

.wf-tree-node.active {
  background: rgba(var(--v-theme-primary), 0.08);
}

.wf-tree-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wf-step-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: #fff;
}

.wf-step-line {
  width: 1px;
  flex: 1;
  min-height: 18px;
  margin-top: 4px;
  background: rgba(var(--v-theme-on-surface), 0.22);
}

.wf-tree-node.completed .wf-step-line {
  background: rgba(var(--v-theme-success), 0.5);
}

.wf-tree-node.active .wf-step-index {
  border-color: rgb(var(--v-theme-primary));
  color: #fff;
  background: rgb(var(--v-theme-primary));
}

.wf-tree-node.active .wf-step-title {
  color: #8d1b3d !important;
  font-weight: 500;
}

.wf-tree-node.active:not(.completed) .wf-step-sub {
  color: #8d1b3d !important;
  opacity: 1;
}

.wf-tree-node.completed .wf-step-sub {
  color: rgb(var(--v-theme-success));
  opacity: 1;
  font-weight: 600;
}

.wf-step-title { font-size: 14px; font-weight: 600; line-height: 1.2; }
.wf-step-sub {
  font-size: 12px;
  opacity: .75;
  margin-top: 2px;
}

.wf-step-tail {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.wf-left-card { position: sticky; top: 88px; }

.wf-right-stack {
  display: grid;
  gap: 12px;
  position: sticky;
  top: 88px;
}

.wf-kv {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.wf-k {
  font-size: 12px;
  opacity: .75;
}

.wf-v {
  font-size: 13px;
  text-align: right;
}

.wf-chip-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.field-item {
  border-color: rgba(var(--v-theme-on-surface), 0.08) !important;
  background-color: #fafafa;
  transition: all 0.2s ease;
}

.field-item:hover {
  border-color: rgba(var(--v-theme-primary), 0.3) !important;
}

.field-label {
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.875rem;
}

.field-value {
  min-height: 40px;
  display: flex;
  align-items: center;
}

.field-description {
  font-style: italic;
}

.form-submission-grid {
  display: grid;
  gap: 1rem;
}

@media (min-width: 960px) {
  .form-submission-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style> 